import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('onerik_token'));
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Set up axios default header when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem('onerik_token', token);
    } else {
      delete axios.defaults.headers.common['Authorization'];
      localStorage.removeItem('onerik_token');
    }
  }, [token]);

  // Check if user is logged in on mount - only once
  useEffect(() => {
    if (initialized) return;
    
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 5000
          });
          setUser(response.data);
        } catch (error) {
          console.error('Auth check failed:', error);
          // Token is invalid, clear it
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
      setInitialized(true);
    };

    checkAuth();
  }, [token, initialized]);

  const register = async (email, password, fullName, accountType = 'personal', phone = '') => {
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email,
        password,
        full_name: fullName,
        account_type: accountType,
        phone: phone
      });
      
      setToken(response.data.token);
      setUser(response.data.user);
      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const login = async (email, password, totpCode = null) => {
    try {
      const payload = { email, password };
      if (totpCode) {
        payload.totp_code = totpCode;
      }
      
      const response = await axios.post(`${API}/auth/login`, payload);
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        return { 
          success: false, 
          requires_2fa: true,
          message: 'Two-factor authentication required'
        };
      }
      
      setToken(response.data.token);
      setUser(response.data.user);
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('onerik_token');
  };

  const refreshUser = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000
      });
      setUser(response.data);
    } catch (error) {
      console.error('Refresh user failed:', error);
    }
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    register,
    login,
    logout,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
